/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect } from 'react'
import { useForm, type SubmitHandler, Controller } from 'react-hook-form'
import Select, { type SingleValue } from 'react-select'

import { Flex, Input, InputGroup, MarkInput } from '@components'
import Button from '@components/Button'
import FileInput from '@components/FileInput'
import Modal from '@components/Modal'
import { successMessages } from '@constants/feedbackMessages'
import { Dialog } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { addPopupsSchema } from '@schemas/addPopups.schema'
import { classesApi } from '@services/ClassesApi'
import { popupsApi } from '@services/PopupsApi'
import { closeModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { inputIsValid } from '@utils/helpers'
import { getCssStyle, pixelsToRem } from '@utils/helpers/styleHelpers'

type Inputs = {
	accessTime: number | string
	link: string
	classId: SingleValue<{ value: number; label: string }>
	mobilePicture: Array<Blob>
	desktopPicture: Array<Blob>
	forPaidClasses: string
	onOpen: string
	onClose: string
	delay: string
	startAt: string
	endAt: string
}

const styles = {
	container:
		'relative inline-block align-bottom bg-white rounded-lg pl-10 pr-10 pt-6 pb-9 text-left overflow-hidden shadow-xl transform transition-all my-8 sm:align-middle',
	title: 'text-lg leading-6 font-medium text-gray-900',
	subTitle: 'text-gray-500 mt-1',
	buttonGroup: 'mt-7 flex gap-4 justify-end',
}

export default function AddPopups() {
	const dispatch = useAppDispatch()

	const {
		modal: { modalName },
	} = useAppSelector((state) => state)

	const { data: dataClass } = classesApi.useListQuery({ limit: 10000 })

	const [addPopups, { isLoading, isSuccess }] = popupsApi.useAddMutation()

	const {
		register,
		control,
		setValue,
		reset,
		handleSubmit,
		formState: { errors, touchedFields },
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(addPopupsSchema),
	})

	const onSubmit: SubmitHandler<Inputs> = (data) => {
		const dataPopups = {
			classId: data?.classId?.value,
			accessTime: data?.accessTime || '1',
			link: data.link,
			endAt: data.endAt,
			startAt: data.startAt,
			delay: data.delay,
			mobilePicture: data.mobilePicture,
			desktopPicture: data.desktopPicture,
			forPaidClasses: !!data.forPaidClasses,
			onOpen: !!data.onOpen,
			onClose: !!data.onClose,
		}

		addPopups(dataPopups)

		if (isSuccess) {
			reset()
		}
	}

	useEffect(() => {
		if (isSuccess) {
			dispatch(closeModal())
			dispatch(notificationCreators.show(successMessages.storedPopups))
		}
	}, [isSuccess, dispatch])

	useEffect(() => {
		if (!modalName) {
			reset()
		}
	}, [modalName, reset])

	return (
		<Modal name='addPopups'>
			<Dialog.Panel
				style={getCssStyle(
					{ name: 'overflowY', value: 'auto' },
					{ name: 'maxWidth', value: pixelsToRem(630) },
					{ name: 'maxHeight', value: pixelsToRem(600) },
					{ name: 'width', value: '100%' }
				)}
				className={`${styles.container} modal-scroll`}
			>
				<p className={styles.title}>Adicionar Popup</p>
				<p className={styles.subTitle}>
					Preencha os campos abaixo para adicionar um novo popup
				</p>

				<form onSubmit={handleSubmit(onSubmit)}>
					<InputGroup label='Turma' id='classId' className='w-full mt-6'>
						<Controller
							control={control}
							name='classId'
							render={({
								field: { value, onBlur },
								fieldState: { isTouched, error },
							}) => {
								return (
									<Select
										onChange={(v) => setValue('classId', v)}
										options={
											dataClass?.data
												? dataClass.data.map((item) => ({
														value: item.id,
														label: item.name,
												  }))
												: []
										}
										value={value}
										onBlur={onBlur}
										styles={{
											input: (base) => ({
												...base,
												borderRadius: '6px',
												paddingLeft: '2.5px',
												paddingRight: '2.5px',
												paddingTop: '3.5px',
												paddingBottom: '3.5px',
											}),
											control: (controlStyles) => ({
												...controlStyles,
												borderColor: (() => {
													const isValid = inputIsValid(error, isTouched)

													if (typeof isValid === 'undefined') {
														return '#D1D5DB !important'
													}

													if (!isValid) {
														return '#F44545 !important'
													}

													return '#15CA71 !important'
												})(),
											}),
											option: (optionStyle, { isFocused }) => ({
												...optionStyle,
												backgroundColor: isFocused ? '#1673FA' : '#fff',
												color: isFocused ? '#fff' : '#000',
											}),
										}}
										placeholder=''
										classNamePrefix='react-select'
									/>
								)
							}}
						/>
					</InputGroup>

					<InputGroup className='mt-4' label='Link' id='link'>
						<Input
							isValid={inputIsValid(errors.link, touchedFields.link)}
							{...register('link')}
							type='url'
						/>
					</InputGroup>

					<Controller
						control={control}
						name='desktopPicture'
						render={({ field, fieldState: { error } }) => {
							return (
								<InputGroup
									className='mt-4'
									label='Imagem Desktop'
									id='desktopPicture'
								>
									<FileInput
										mt='mt-4'
										onChange={(v) => {
											field.onChange(v)
										}}
										accept='image/png, image/jpeg'
										isValid={!error}
									>
										Imagem
									</FileInput>
								</InputGroup>
							)
						}}
					/>

					<Controller
						control={control}
						name='mobilePicture'
						render={({ field, fieldState: { error } }) => {
							return (
								<InputGroup
									className='mt-4'
									label='Imagem Mobile'
									id='mobilePicture'
								>
									<FileInput
										mt='mt-4'
										onChange={(v) => {
											field.onChange(v)
										}}
										accept='image/png, image/jpeg'
										isValid={!error}
									>
										Imagem
									</FileInput>
								</InputGroup>
							)
						}}
					/>

					<Flex className='mt-4 gap-4'>
						<InputGroup label='Data inicial' id='startAt'>
							<Input
								type='date'
								maxLength={4}
								{...register('startAt')}
								// onChange={(e) => setStartDate(e.currentTarget.value)}
							/>
						</InputGroup>
						<InputGroup label='Data final' id='endAt'>
							<Input
								type='date'
								maxLength={4}
								{...register('endAt')}
								// onChange={(e) => setEndDate(e.currentTarget.value)}
							/>
						</InputGroup>
						<InputGroup label='Delay (em minutos)' id='delay'>
							<Input
								isValid={inputIsValid(errors.delay, touchedFields.delay)}
								{...register('delay')}
								type='number'
								min={1}
								placeholder='-'
							/>
						</InputGroup>
					</Flex>

					<Flex className='mt-2 flex-col'>
						{errors.startAt && (
							<span className='text-red-500 text-sm mt-2'>
								{errors.startAt.message}
							</span>
						)}
						{errors.endAt && (
							<span className='text-red-500 text-sm mt-2'>
								{errors.endAt.message}
							</span>
						)}
					</Flex>

					<Controller
						control={control}
						name='forPaidClasses'
						render={({ field: { onBlur } }) => {
							return (
								<>
									<MarkInput
										className='mt-4'
										type='checkbox'
										multiCheckbox
										itens={[
											{
												id: '1',
												title: 'Somente para alunos com assinaturas pagas',
											},
										]}
										onBlur={onBlur}
										onChange={(e) => {
											setValue(
												'forPaidClasses',
												e.currentTarget.checked as unknown as string
											)
										}}
									/>
								</>
							)
						}}
					/>

					<Controller
						control={control}
						name='onOpen'
						render={({ field: { onBlur } }) => {
							return (
								<>
									<MarkInput
										className='mt-4'
										type='checkbox'
										multiCheckbox
										itens={[
											{
												id: '2',
												title: 'Mostrar ao efetuar o login',
											},
										]}
										onBlur={onBlur}
										onChange={(e) => {
											setValue(
												'onOpen',
												e.currentTarget.checked as unknown as string
											)
										}}
									/>
								</>
							)
						}}
					/>

					<Controller
						control={control}
						name='onClose'
						render={({ field: { onBlur } }) => {
							return (
								<>
									<MarkInput
										className='mt-4'
										type='checkbox'
										multiCheckbox
										itens={[
											{
												id: '3',
												title: 'Mostrar ao tentar fechar a página',
											},
										]}
										onBlur={onBlur}
										onChange={(e) => {
											setValue(
												'onClose',
												e.currentTarget.checked as unknown as string
											)
										}}
									/>
								</>
							)
						}}
					/>

					<div className={styles.buttonGroup}>
						<Button
							type='button'
							onClick={() => {
								dispatch(closeModal())
							}}
							variant='outline'
							disabled={false}
						>
							Cancelar
						</Button>

						<Button type='submit' isLoading={isLoading} disabled={!modalName}>
							Adicionar Popup
						</Button>
					</div>
				</form>
			</Dialog.Panel>
		</Modal>
	)
}
